import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Stack, Typography, Chip, Skeleton, Divider, LinearProgress, Tooltip,
} from '@mui/material';
import CheckCircleIcon         from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutoAwesomeIcon         from '@mui/icons-material/AutoAwesome';
import BadgeIcon               from '@mui/icons-material/Badge';
import WorkIcon                from '@mui/icons-material/Work';
import AttachMoneyIcon         from '@mui/icons-material/AttachMoney';
import ReceiptIcon             from '@mui/icons-material/Receipt';
import LinkIcon                from '@mui/icons-material/Link';
import GavelIcon               from '@mui/icons-material/Gavel';
import ShieldIcon              from '@mui/icons-material/Shield';
import HealthAndSafetyIcon     from '@mui/icons-material/HealthAndSafety';
import AccountBalanceIcon      from '@mui/icons-material/AccountBalance';
import { useSnackbar } from 'notistack';
import { fetchRecords } from '../services/api';

// Indian fiscal year for given date — "2026-27"
const currentFY = () => {
  const d = new Date();
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  const start = m >= 4 ? y : y - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

const STEPS = [
  { key: 'person',      label: 'Person Profile',          icon: <BadgeIcon />,           tier: 'required',  auto: false, source: null, hint: 'Identity, contact, PAN, Aadhar' },
  { key: 'assignment',  label: 'Assignment',              icon: <WorkIcon />,            tier: 'required',  auto: false, source: 'ASSIGNMENT',           hint: 'Job, department, salary template' },
  { key: 'salary',      label: 'Salary',                  icon: <AttachMoneyIcon />,     tier: 'required',  auto: true,  source: 'SALARY',               hint: 'Auto-created from template' },
  { key: 'element_entry',label:'Element Entries',         icon: <ReceiptIcon />,         tier: 'required',  auto: true,  source: 'ELEMENT_ENTRY',        hint: 'Auto-created from template' },
  { key: 'prel',        label: 'Payroll Relationship',    icon: <LinkIcon />,            tier: 'required',  auto: true,  source: 'PAYROLL_RELATIONSHIP', hint: 'Auto-created on Assignment' },
  { key: 'tax_regime',  label: 'Tax Regime',              icon: <GavelIcon />,           tier: 'required',  auto: true,  source: 'TAX_REGIME_CONFIG',    hint: 'Auto-created · default New regime' },
  { key: 'pf',          label: 'PF Account',              icon: <ShieldIcon />,          tier: 'statutory', auto: false, source: 'PF_ACCOUNT',           hint: 'Needs UAN from employee' },
  { key: 'esi',         label: 'ESI Account',             icon: <HealthAndSafetyIcon />, tier: 'statutory', auto: false, source: 'ESI_ACCOUNT',          hint: 'Needs IP Number from ESIC' },
  { key: 'bank',        label: 'Bank Account',            icon: <AccountBalanceIcon />,  tier: 'statutory', auto: false, source: 'PERSONAL_PAYMENT_METHOD', hint: 'Needs bank details from employee' },
];

export default function PersonOnboardingPanel({ personId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts]   = useState({});

  useEffect(() => {
    if (!personId) return;
    setLoading(true);
    const sources = ['ASSIGNMENT','SALARY','ELEMENT_ENTRY','PAYROLL_RELATIONSHIP',
                     'TAX_REGIME_CONFIG','PF_ACCOUNT','ESI_ACCOUNT','PERSONAL_PAYMENT_METHOD'];
    Promise.all(sources.map(s => fetchRecords(s).catch(() => ({ data: { data: [] } }))))
      .then((res) => {
        const [asn, sal, ee, prel, trc, pf, esi, ppm] = res.map(r => r.data?.data || []);
        const assignmentIds = asn.filter(a => a.person_id === personId).map(a => a.assignment_id);
        const fy = currentFY();
        setCounts({
          person:         1,                                                                                  // We're on this person
          assignment:     asn.filter(a => a.person_id === personId).length,
          salary:         sal.filter(s => assignmentIds.includes(s.assignment_id)).length,
          element_entry:  ee.filter(e  => assignmentIds.includes(e.assignment_id)).length,
          prel:           prel.filter(p => p.person_id === personId).length,
          tax_regime:     trc.filter(t  => t.person_id === personId && t.fiscal_year === fy).length,
          pf:             pf.filter(p   => assignmentIds.includes(p.assignment_id)).length,
          esi:            esi.filter(e  => assignmentIds.includes(e.assignment_id)).length,
          bank:           ppm.filter(p  => assignmentIds.includes(p.assignment_id)).length,
        });
        setLoading(false);
      })
      .catch(e => {
        enqueueSnackbar('Failed to load onboarding data: ' + e.message, { variant: 'error' });
        setLoading(false);
      });
  }, [personId, enqueueSnackbar]);

  const requiredSteps  = STEPS.filter(s => s.tier === 'required');
  const statutorySteps = STEPS.filter(s => s.tier === 'statutory');

  const requiredDone  = requiredSteps.filter(s => (counts[s.key] || 0) > 0).length;
  const statutoryDone = statutorySteps.filter(s => (counts[s.key] || 0) > 0).length;
  const totalDone     = requiredDone + statutoryDone;
  const totalCount    = STEPS.length;
  const pct           = totalCount > 0 ? (totalDone / totalCount) * 100 : 0;

  if (loading) {
    return (
      <Box sx={{ p: 2.5, bgcolor: '#FAFBFC' }}>
        <Skeleton height={32} width={280} sx={{ mb: 2 }} />
        {[...Array(5)].map((_, i) => <Skeleton key={i} height={48} sx={{ mb: 1, borderRadius: 1 }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5, bgcolor: '#FAFBFC', borderTop: '1.5px solid #E5E7EB' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Typography fontWeight={700} fontSize={14} color="#111827">
            Onboarding Checklist
          </Typography>
          <Chip
            label={`${totalDone}/${totalCount} Complete`}
            size="small"
            sx={{
              height: 22, fontSize: 11, fontWeight: 700, borderRadius: '8px',
              bgcolor: pct === 100 ? '#dcfce7' : pct >= 50 ? '#FEF3C7' : '#FEF2F2',
              color:   pct === 100 ? '#15803d' : pct >= 50 ? '#92400E' : '#991B1B',
              border:  pct === 100 ? '1px solid #bbf7d0'
                     : pct >= 50 ? '1px solid #FDE68A' : '1px solid #FECACA',
            }}
          />
        </Stack>
        <Typography fontSize={11.5} color="text.secondary">
          Required + Statutory · auto-cascaded items marked <AutoAwesomeIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: '#16a34a' }} />
        </Typography>
      </Stack>

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 6, borderRadius: 3,
            bgcolor: '#F3F4F6',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              background: pct === 100
                ? 'linear-gradient(90deg,#16a34a,#22c55e)'
                : pct >= 50
                  ? 'linear-gradient(90deg,#F59E0B,#FBBF24)'
                  : 'linear-gradient(90deg,#DC2626,#EF4444)',
            },
          }}
        />
      </Box>

      {/* Tier sections */}
      {[
        { title: 'Required (auto-cascaded)', steps: requiredSteps, done: requiredDone, total: requiredSteps.length },
        { title: 'Statutory (manual)',       steps: statutorySteps, done: statutoryDone, total: statutorySteps.length },
      ].map(group => (
        <Box key={group.title} sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.8 }}>
            <Typography fontSize={10.5} fontWeight={700} color="#6B7280"
              sx={{ letterSpacing: 0.6, textTransform: 'uppercase' }}>
              {group.title}
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: '#E5E7EB' }} />
            <Typography fontSize={10.5} fontWeight={700} color="#6B7280">
              {group.done}/{group.total}
            </Typography>
          </Stack>

          <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1.5px solid #E5E7EB', overflow: 'hidden' }}>
            {group.steps.map((step, i) => {
              const count = counts[step.key] || 0;
              const done  = count > 0;
              return (
                <React.Fragment key={step.key}>
                  {i > 0 && <Divider />}
                  <Stack direction="row" alignItems="center" spacing={2}
                    sx={{ px: 2, py: 1.2, '&:hover': { bgcolor: '#F9FAFB' } }}>
                    {/* Status icon */}
                    <Box sx={{ width: 22, display: 'flex', justifyContent: 'center' }}>
                      {done
                        ? <CheckCircleIcon sx={{ fontSize: 20, color: '#22c55e' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#94A3B8' }} />}
                    </Box>

                    {/* Item icon */}
                    <Box sx={{ width: 28, height: 28, borderRadius: '7px',
                      bgcolor: done ? '#F0FDF4' : '#F3F4F6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: 15, color: done ? '#16a34a' : '#6B7280' } })}
                    </Box>

                    {/* Label + hint */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Typography fontSize={13} fontWeight={600} color={done ? '#111827' : '#374151'}>
                          {step.label}
                        </Typography>
                        {step.auto && (
                          <Tooltip title="Automatically created when Assignment is added" arrow>
                            <AutoAwesomeIcon sx={{ fontSize: 12, color: '#16a34a' }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography fontSize={10.5} color="#9CA3AF">{step.hint}</Typography>
                    </Box>

                    {/* Count chip */}
                    <Chip
                      label={done ? `${count} record${count > 1 ? 's' : ''}` : 'Pending'}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10.5, fontWeight: 700, borderRadius: '6px',
                        bgcolor: done ? '#dcfce7' : '#F3F4F6',
                        color:   done ? '#15803d' : '#6B7280',
                        border:  done ? '1px solid #bbf7d0' : '1px solid #E5E7EB',
                      }}
                    />
                  </Stack>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      ))}

      {/* Footer message */}
      {pct < 100 && (
        <Box sx={{ mt: 1.5, p: 1.2, bgcolor: '#FFFBEB', borderRadius: '8px',
          border: '1px solid #FDE68A' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F59E0B' }} />
            <Typography fontSize={11.5} color="#78350F">
              {totalCount - totalDone} item{totalCount - totalDone > 1 ? 's' : ''} pending.
              {' '}{statutoryDone < statutorySteps.length
                ? 'Add PF / ESI / Bank records to complete statutory setup.'
                : 'Some required items missing — review above.'}
            </Typography>
          </Stack>
        </Box>
      )}
      {pct === 100 && (
        <Box sx={{ mt: 1.5, p: 1.2, bgcolor: '#F0FDF4', borderRadius: '8px',
          border: '1px solid #BBF7D0' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
            <Typography fontSize={11.5} fontWeight={600} color="#15803d">
              Employee fully onboarded — ready for payroll.
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
